import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBranchContext } from "@/hooks/useBranchContext";

export interface TATAlertRecipient {
  id: string;
  escalation_level: number;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export const useTATAlertRecipients = () => {
  const queryClient = useQueryClient();
  const { currentBranchId } = useBranchContext();

  // Fetch all recipients
  const { data: recipients, isLoading } = useQuery({
    queryKey: ["tat-alert-recipients", currentBranchId],
    queryFn: async () => {
      if (currentBranchId == null) return [];
      const { data, error } = await supabase
        .from("tat_alert_recipients")
        .select("*")
        .eq("branch_id", currentBranchId)
        .order("escalation_level");
      
      if (error) throw error;
      return data as TATAlertRecipient[];
    },
  });

  // Update recipients for a specific level
  const updateLevelRecipients = useMutation({
    mutationFn: async ({ 
      level, 
      userIds 
    }: { 
      level: number; 
      userIds: string[] 
    }) => {
      if (currentBranchId == null) return;
      // Delete existing recipients for this level
      await supabase
        .from("tat_alert_recipients")
        .delete()
        .eq("escalation_level", level)
        .eq("branch_id", currentBranchId);

      // Insert new recipients
      if (userIds.length > 0) {
        const { error } = await supabase
          .from("tat_alert_recipients")
          .insert(
            userIds.map(userId => ({
              escalation_level: level,
              user_id: userId,
              branch_id: currentBranchId,
            }))
          );
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tat-alert-recipients", currentBranchId] });
    },
  });

  // Get recipients by level
  const getRecipientsByLevel = useCallback((level: number): string[] => {
    if (!recipients) return [];
    return recipients
      .filter(r => r.escalation_level === level)
      .map(r => r.user_id);
  }, [recipients]);

  return {
    recipients,
    isLoading,
    updateLevelRecipients,
    getRecipientsByLevel,
  };
};
